/**
 * Devtools guard — detects browser devtools / mobile inspectors and notifies caller.
 *
 * Detection vectors (any one trips the alarm):
 *  1. eruda / vConsole / weinre / FrontBug / mDebug globals (mobile inspectors).
 *  2. window.outer − inner size diff > 160 px (classic desktop devtools).
 *  3. console.log-getter trick: assigning a getter to a logged object's
 *     property; the getter fires only when devtools renders the object.
 *  4. debugger-statement timing: a `debugger;` inside a Function takes
 *     >100 ms only when devtools are open (paused execution).
 *  5. Hostile-by-default browsers (Kiwi / Lemur) — full Chromium DevTools,
 *     impossible to detect reliably while closed, so we treat them as
 *     always-open.
 *
 * On trip:
 *   • setState(true) is called.
 *   • Caller is expected to destroy any active media (iframe / blob URL / video src).
 *
 * IMPORTANT — no cross-reload persistence:
 *   The trip flag lives in React state only. If the user closes Eruda /
 *   DevTools and refreshes the page, the next mount re-runs the checks
 *   from scratch; if nothing is detected the player plays again. This is
 *   intentional: persisting the flag in sessionStorage made the player
 *   stay dead until the tab was closed, which broke legitimate refresh.
 *
 * Safe in SSR — every check is guarded by typeof window !== "undefined".
 */
import { useEffect, useState } from "react";

const MOBILE_INSPECTOR_GLOBALS = ["eruda", "vConsole", "weinre", "FrontBug", "mDebug"];

function hasMobileInspector(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as Record<string, unknown>;
  for (const k of MOBILE_INSPECTOR_GLOBALS) {
    if (w[k]) return true;
  }
  // Eruda injects a #eruda div into the DOM.
  if (typeof document !== "undefined" && document.getElementById("eruda")) return true;
  return false;
}

function hasSizeGap(): boolean {
  if (typeof window === "undefined") return false;
  // Mobile UA → orientation flips skew outer/inner; rely on inspector + timing only.
  const ua = navigator.userAgent || "";
  if (/android|iphone|ipad|ipod|mobile/i.test(ua)) return false;
  const wGap = Math.abs((window.outerWidth || 0) - window.innerWidth);
  const hGap = Math.abs((window.outerHeight || 0) - window.innerHeight);
  return wGap > 160 || hGap > 160;
}

/**
 * Kiwi Browser و Lemur Browser على أندرويد يوفّران DevTools كاملة (Chromium
 * desktop-grade) دون أن يحقنا eruda، ولا توجد طريقة موثوقة لاكتشاف ما إذا
 * كانت لوحة DevTools مفتوحة فيهما. القرار الآمن: نعتبر هذه المتصفحات
 * "معاديّة افتراضياً" ونرفض تشغيل المحتوى المحميّ عليها.
 */
function isHostileDevtoolsBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Kiwi Browser → "... Chrome/... Mobile Safari/... KiwiBrowser"
  // Lemur Browser → "... Lemur/..."
  return /\b(KiwiBrowser|Kiwi\/|Lemur)\b/i.test(ua);
}

let _consoleTrip = false;
function installConsoleTrap(onTrip: () => void) {
  if (typeof window === "undefined") return;
  try {
    const bait: Record<string, unknown> = {};
    Object.defineProperty(bait, "id", {
      get() {
        if (!_consoleTrip) {
          _consoleTrip = true;
          onTrip();
        }
        return "rawan-trap";
      },
    });
    // Logged silently — devtools "Console" panel triggers the getter on render.
    setInterval(() => {
      // eslint-disable-next-line no-console
      console.log(bait);
      // eslint-disable-next-line no-console
      console.clear?.();
    }, 1500);
  } catch {
    /* ignore */
  }
}

function debuggerTiming(): boolean {
  try {
    const t0 = performance.now();
    // eslint-disable-next-line no-new-func, no-debugger
    new Function("debugger")();
    return performance.now() - t0 > 100;
  } catch {
    return false;
  }
}

/**
 * React hook — returns true once devtools / inspectors are detected.
 * Sticky within the same React tree only (in-memory). On full page reload
 * we start fresh so a user who closed DevTools can play again.
 */
export function useDevtoolsOpen(): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    const trip = () => {
      if (cancelled) return;
      setOpen(true);
    };

    // Hostile browsers (Kiwi/Lemur) — block immediately, no probing needed.
    if (isHostileDevtoolsBrowser()) {
      trip();
      return;
    }

    // Initial synchronous checks.
    if (hasMobileInspector() || hasSizeGap() || debuggerTiming()) {
      trip();
      return;
    }

    installConsoleTrap(trip);

    const iv = setInterval(() => {
      if (cancelled) return;
      if (hasMobileInspector() || hasSizeGap() || debuggerTiming()) {
        trip();
      }
    }, 1200);

    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, []);

  return open;
}
